import React, { useState, useEffect, useCallback, useRef } from "react";
import "../../Styles/ThallikiVandanamTable.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { thallikiVandanamAPI } from "../../api/index";
import {
  FaUserFriends,
  FaCheckCircle,
  FaRegClock,
  FaRupeeSign,
  FaSearch,
  FaMapMarkerAlt,
  FaFilter,
  FaSyncAlt,
  FaUserCircle,
  FaCalendarAlt,
  FaEdit,
  FaTrashAlt,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaHandHoldingHeart,
  FaUpload,
  FaDownload,
  FaImage,
} from "react-icons/fa";

// Kept only for the filter dropdown / Add-Edit mandal <select> options.
// No longer used to derive display values — the API returns
// mandal_name / village_name directly, so we trust those.
const MANDAL_NAMES = ["Kandukur", "Lingasamudram", "Gudluru", "Ulavapadu", "Voletivaripalem"];

const STATUS_OPTIONS = ["pending", "approved", "rejected", "disbursed"];
const STATUS_LABEL = { pending: "Pending", approved: "Approved", rejected: "Rejected", disbursed: "Disbursed" };

// ---------- Excel template settings ----------
// Headings follow the Add Beneficiary form fields (same order as the form).
// CONFIRM these with the backend's bulk-upload parser. If it expects different
// heading names, change them here only.
const TEMPLATE_HEADERS = [
  "Mother's Name",
  "Relation",
  "Voter Id",
  "Student Name",
  "School Name",
  "Class / Grade",
  "Aadhaar Number",
  "Mobile Number",
  "Bank Account Number",
  "IFSC Code",
  "Annual Amount (₹)",
  "Village",
  "Mandal",
  "Date",
  "Status",
  "Photo URL",
  "Supporting Document URL",
  "Remarks",
];

const TEMPLATE_COLUMN_WIDTHS = [
  { wch: 26 }, // Mother's Name
  { wch: 22 }, // Relation
  { wch: 16 }, // Voter Id
  { wch: 26 }, // Student Name
  { wch: 32 }, // School Name
  { wch: 16 }, // Class / Grade
  { wch: 18 }, // Aadhaar Number
  { wch: 16 }, // Mobile Number
  { wch: 24 }, // Bank Account Number
  { wch: 14 }, // IFSC Code
  { wch: 20 }, // Annual Amount
  { wch: 22 }, // Village
  { wch: 20 }, // Mandal
  { wch: 14 }, // Date
  { wch: 14 }, // Status
  { wch: 38 }, // Photo URL
  { wch: 38 }, // Supporting Document URL
  { wch: 40 }, // Remarks
];

const TEMPLATE_INSTRUCTIONS = [
  ["Column", "Required", "Allowed values / Rules"],
  ["Mother's Name", "Yes", "3-100 characters. Alphabets, spaces, '.' and ''' only"],
  ["Relation", "Yes", "2-100 characters (e.g. W/O Ramesh, D/O Suresh)"],
  ["Voter Id", "Yes", "3 capital letters + 7 digits (e.g. ABC1234567)"],
  ["Student Name", "Yes", "3-100 characters. Alphabets, spaces, '.' and ''' only"],
  ["School Name", "Yes", "3-150 characters"],
  ["Class / Grade", "Yes", "Up to 30 characters (e.g. 5th Class, 10, LKG)"],
  ["Aadhaar Number", "Yes", "Exactly 12 digits, cannot start with 0 or 1. Format the cell as Text in Excel"],
  ["Mobile Number", "Yes", "Exactly 10 digits, must start with 6, 7, 8 or 9. Format the cell as Text in Excel"],
  ["Bank Account Number", "Yes", "Digits only, 9-18 digits. Format the cell as Text in Excel"],
  ["IFSC Code", "Yes", "4 capital letters + 0 + 6 letters/digits (e.g. SBIN0001234)"],
  ["Annual Amount (₹)", "Yes", "Fixed amount: 15000"],
  ["Village", "Yes", "Alphabets and spaces only. Must exactly match an existing village under the Mandal"],
  ["Mandal", "Yes", MANDAL_NAMES.join(", ")],
  ["Date", "Yes", "Format: YYYY-MM-DD (e.g. 2026-10-03)"],
  ["Status", "Yes", STATUS_OPTIONS.join(", ")],
  ["Photo URL", "No", "Optional. A link starting with http:// or https://"],
  ["Supporting Document URL", "No", "Optional. A link starting with http:// or https://"],
  ["Remarks", "No", "Optional. If filled: 5-500 characters"],
];

const FALLBACK_IMG =
  "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Crect width='60' height='60' fill='%23e5e7eb'/%3E%3C/svg%3E";

const num = (v) => (typeof v === "number" ? v : Number(v) || 0);

const extractList = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.items || d?.results || d?.data || [];
};

const extractTotalCount = (res, fallbackLen) => {
  const d = res?.data;
  return num(d?.total ?? d?.count ?? d?.total_count ?? fallbackLen);
};

const extractErrorMessage = (err) => {
  if (!err.response) {
    return `Network error — could not reach the server (${err.message}).`;
  }
  const status = err.response.status;
  const detail = err.response.data?.detail ?? err.response.data?.message;

  if (status === 404) {
    return typeof detail === "string"
      ? `[404] ${detail} — double check the spelling of the Village/Mandal name.`
      : `[404] Village or Mandal not recognized. Check spelling and try again.`;
  }
  if (typeof detail === "string") return `[${status}] ${detail}`;
  if (Array.isArray(detail)) {
    const parts = detail.slice(0, 3).map((d) => {
      if (d.row !== undefined) return `Row ${d.row}: ${d.reason}`;
      if (d.loc) return `${d.loc.join(".")}: ${d.msg}`;
      return d.msg || d.reason || JSON.stringify(d);
    });
    return `[${status}] ${parts.join(" | ")}`;
  }
  return `[${status}] Request failed — see console for full response body`;
};

const isLikelyUrl = (url) => {
  if (!url.trim()) return true;
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
};

// FIX: use the name fields the backend now returns directly instead of
// re-deriving a label from mandal_id/village_id.
const normalizeRow = (row) => ({
  id: row.id,
  motherName: row.mother_name || "Untitled",
  relation: row.relation_name || "",
  voterId: row.epic_no || "",
  studentName: row.student_name || "",
  school: row.school_name || "",
  className: row.class_grade || "",
  aadhaarMasked: row.aadhaar_masked || "",
  mobile: row.mobile_number || "",
  bankAccount: row.bank_account_number || "",
  ifsc: row.ifsc_code || "",
  amount: num(row.amount),
  mandalId: row.mandal_id,
  mandal: row.mandal_name || (row.mandal_id ? `Mandal #${row.mandal_id}` : "—"),
  villageId: row.village_id,
  village: row.village_name || (row.village_id ? `Village #${row.village_id}` : "—"),
  date: row.application_date || "",
  status: row.status || "pending",
  photoUrl: row.photo_url || "",
  documentUrl: row.document_url || "",
  remarks: row.remarks || "",
});

const ThallikiVandanamTable = () => {
  const navigate = useNavigate();

  const [tvData, setTvData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [mandalFilter, setMandalFilter] = useState("All Mandals");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 10;

  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkFileInputRef = useRef(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const fetchTvData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, page_size: PAGE_SIZE };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (mandalFilter !== "All Mandals") params.mandal_name = mandalFilter;
      if (statusFilter !== "All Status") params.status = statusFilter;

      const res = await thallikiVandanamAPI.getAll(params);
      const rows = extractList(res).map(normalizeRow);
      setTvData(rows);
      setTotalCount(extractTotalCount(res, rows.length));
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, mandalFilter, statusFilter]);

  useEffect(() => {
    fetchTvData();
  }, [fetchTvData]);

  const handleResetFilters = () => {
    setSearchTerm("");
    setMandalFilter("All Mandals");
    setStatusFilter("All Status");
    setCurrentPage(1);
  };

  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + tvData.length;

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 3;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);
    if (start > 2) pages.push("...");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push("...");
    pages.push(totalPages);
    return pages;
  };

  const approvedCount = tvData.filter((i) => i.status === "approved").length;
  const pendingCount = tvData.filter((i) => i.status === "pending").length;
  const totalAmount = tvData.reduce((sum, i) => sum + i.amount, 0);

  // FIX: edit form now pre-fills Village/Mandal from the row directly, and
  // carries every field the update payload accepts — relation, voter ID,
  // mobile, bank account, IFSC, document URL, remarks — instead of
  // silently dropping them. Aadhaar is left blank and required on save
  // (see handleEditSave) since GET never returns the real number and the
  // backend rejects updates missing it entirely.
  const openEditModal = (item) => {
    setEditItem(item);
    setEditForm({
      motherName: item.motherName || "",
      relation: item.relation || "",
      voterId: item.voterId || "",
      studentName: item.studentName || "",
      school: item.school || "",
      className: item.className || "",
      aadhaar: "",
      mobile: item.mobile || "",
      bankAccount: item.bankAccount || "",
      ifsc: item.ifsc || "",
      amount: item.amount ?? "",
      mandalName: item.mandal || "",
      villageName: item.village && item.village !== "—" ? item.village : "",
      date: item.date || "",
      status: item.status || "pending",
      photoUrl: item.photoUrl || "",
      documentUrl: item.documentUrl || "",
      remarks: item.remarks || "",
    });
  };

  const closeEditModal = () => {
    setEditItem(null);
    setEditForm(null);
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditSave = async (e) => {
    e.preventDefault();

    const trimmedVillage = editForm.villageName.trim();
    const trimmedAadhaar = editForm.aadhaar.trim();

    if (!editForm.motherName.trim()) {
      toast.error("Mother's Name is required");
      return;
    }
    if (!editForm.studentName.trim()) {
      toast.error("Student Name is required");
      return;
    }
    if (!editForm.school.trim()) {
      toast.error("School Name is required");
      return;
    }
    if (!editForm.className.trim()) {
      toast.error("Class / Grade is required");
      return;
    }
    if (!trimmedVillage) {
      toast.error("Please enter the village name to save");
      return;
    }
    if (!editForm.mandalName) {
      toast.error("Please select a Mandal");
      return;
    }
    // FIX: Aadhaar is mandatory on save — backend rejects updates missing
    // it, and GET never returns the real number, so it must be re-typed.
    if (!trimmedAadhaar) {
      toast.error("Please re-enter the 12-digit Aadhaar Number to save changes");
      return;
    }
    if (trimmedAadhaar.length !== 12 || !/^\d{12}$/.test(trimmedAadhaar)) {
      toast.error("Aadhaar Number must be exactly 12 digits");
      return;
    }
    if (editForm.mobile && editForm.mobile.length !== 10) {
      toast.error("Mobile Number must be exactly 10 digits");
      return;
    }
    if (editForm.photoUrl.trim() && !isLikelyUrl(editForm.photoUrl.trim())) {
      toast.error("Enter a valid Photo URL starting with https://");
      return;
    }
    if (editForm.documentUrl.trim() && !isLikelyUrl(editForm.documentUrl.trim())) {
      toast.error("Enter a valid Document URL starting with https://");
      return;
    }

    const payload = {
      mother_name: editForm.motherName.trim(),
      relation_name: editForm.relation.trim() || undefined,
      epic_no: editForm.voterId.trim() || undefined,
      student_name: editForm.studentName.trim(),
      school_name: editForm.school.trim(),
      class_grade: editForm.className.trim(),
      // FIX: always send a real value now — never undefined.
      aadhaar_number: trimmedAadhaar,
      mobile_number: editForm.mobile.trim() || undefined,
      bank_account_number: editForm.bankAccount.trim() || undefined,
      ifsc_code: editForm.ifsc.trim() || undefined,
      amount: editForm.amount ? Number(editForm.amount) : undefined,
      mandal_name: editForm.mandalName,
      village_name: trimmedVillage,
      application_date: editForm.date || undefined,
      status: editForm.status,
      photo_url: editForm.photoUrl.trim() || undefined,
      document_url: editForm.documentUrl.trim() || undefined,
      remarks: editForm.remarks.trim() || undefined,
    };

    setSaving(true);
    try {
      await thallikiVandanamAPI.update(editItem.id, payload);
      toast.success("Beneficiary updated");
      closeEditModal();
      fetchTvData();
    } catch (err) {
      console.error("Thalliki Vandanam update payload sent:", payload);
      toast.error(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (item) => setDeleteItem(item);
  const closeDeleteModal = () => setDeleteItem(null);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await thallikiVandanamAPI.delete(deleteItem.id);
      toast.success("Deleted successfully");
      closeDeleteModal();
      fetchTvData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const openImagePreview = (url) => setImagePreviewUrl(url);
  const closeImagePreview = () => setImagePreviewUrl(null);

  /* ---------- Download Excel template (headings only) ---------- */
  const handleDownloadTemplate = () => {
    try {
      // Sheet 1: Template (headings only, ready to fill)
      const templateSheet = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
      templateSheet["!cols"] = TEMPLATE_COLUMN_WIDTHS;

      // Sheet 2: Instructions (allowed values and rules)
      const instructionSheet = XLSX.utils.aoa_to_sheet(TEMPLATE_INSTRUCTIONS);
      instructionSheet["!cols"] = [{ wch: 26 }, { wch: 10 }, { wch: 90 }];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, templateSheet, "Template");
      XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");

      XLSX.writeFile(workbook, "Thalliki_Vandanam_Template.xlsx");
      toast.success("Template downloaded successfully");
    } catch (err) {
      console.error("Template download failed:", err);
      toast.error("Could not download the template");
    }
  };

  const handleBulkUploadClick = () => bulkFileInputRef.current?.click();

  const handleBulkFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      toast.error("Please select a valid Excel file (.xlsx or .xls)");
      return;
    }

    setBulkUploading(true);
    try {
      const res = await thallikiVandanamAPI.bulkUpload(file);
      toast.success(`Import complete — ${num(res.data?.inserted)} beneficiaries added`);
      fetchTvData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setBulkUploading(false);
    }
  };

  return (
    <div className="tv-page">
      <input
        ref={bulkFileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        onChange={handleBulkFileSelected}
      />

      <div className="tv-header">
        <div className="tv-header-left">
          <div className="tv-header-icon">
            <FaHandHoldingHeart />
          </div>
          <div>
            <h2 className="tv-title">Thalliki Vandanam Beneficiaries</h2>
            <p className="tv-subtitle">Manage and track beneficiaries under Thalliki Vandanam scheme</p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="tv-add-btn"
            onClick={handleDownloadTemplate}
            style={{ background: "#16a34a" }}
          >
            <span className="tv-add-plus">
              <FaDownload />
            </span>{" "}
            Download Template
          </button>

          <button
            className="tv-add-btn"
            onClick={handleBulkUploadClick}
            disabled={bulkUploading}
            style={{ background: "#0b3d24" }}
          >
            <span className="tv-add-plus">{bulkUploading ? "⏳" : <FaUpload />}</span>{" "}
            {bulkUploading ? "Uploading..." : "Bulk Upload (Excel)"}
          </button>

          <button className="tv-add-btn" onClick={() => navigate("/dashboard/ThallikiVandanamForm")}>
            <span className="tv-add-plus">+</span> Add New Beneficiary
          </button>
        </div>
      </div>

      <div className="tv-scroll-body">
        <div className="tv-stats-row">
          <div className="tv-stat-card">
            <div className="tv-stat-icon tv-icon-purple">
              <FaUserFriends />
            </div>
            <div>
              <p className="tv-stat-label">Total Beneficiaries</p>
              <h3 className="tv-stat-value">{totalCount}</h3>
              <p className="tv-stat-sub">All time records</p>
            </div>
          </div>

          <div className="tv-stat-card">
            <div className="tv-stat-icon tv-icon-green">
              <FaCheckCircle />
            </div>
            <div>
              <p className="tv-stat-label">Approved Beneficiaries</p>
              <h3 className="tv-stat-value">{approvedCount}</h3>
              <p className="tv-stat-sub tv-sub-green">
                {tvData.length ? ((approvedCount / tvData.length) * 100).toFixed(1) : 0}% of page
              </p>
            </div>
          </div>

          <div className="tv-stat-card">
            <div className="tv-stat-icon tv-icon-orange">
              <FaRegClock />
            </div>
            <div>
              <p className="tv-stat-label">Pending Beneficiaries</p>
              <h3 className="tv-stat-value">{pendingCount}</h3>
              <p className="tv-stat-sub tv-sub-orange">
                {tvData.length ? ((pendingCount / tvData.length) * 100).toFixed(1) : 0}% of page
              </p>
            </div>
          </div>

          <div className="tv-stat-card">
            <div className="tv-stat-icon tv-icon-purple2">
              <FaRupeeSign />
            </div>
            <div>
              <p className="tv-stat-label">Total Amount Disbursed</p>
              <h3 className="tv-stat-value">₹{totalAmount.toLocaleString("en-IN")}</h3>
              <p className="tv-stat-sub tv-sub-purple">This page</p>
            </div>
          </div>
        </div>

        <div className="tv-filter-section">
          <div className="tv-search-wrapper">
            <FaSearch className="tv-search-icon" />
            <input
              type="text"
              placeholder="Search Beneficiary..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="tv-select-wrapper">
            <FaMapMarkerAlt className="tv-select-icon" />
            <select
              value={mandalFilter}
              onChange={(e) => {
                setMandalFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option>All Mandals</option>
              {MANDAL_NAMES.map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
          </div>

          <div className="tv-select-wrapper">
            <FaFilter className="tv-select-icon" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="All Status">All Status</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </select>
          </div>

          <button className="tv-reset-btn" onClick={handleResetFilters}>
            <FaSyncAlt /> Reset Filters
          </button>
        </div>

        <div className="tv-table-wrapper">
          <table className="tv-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Mother's Name</th>
                <th>Student Name</th>
                <th>School</th>
                <th>Class</th>
                <th>Amount</th>
                <th>Village</th>
                <th>Mandal</th>
                <th>Date</th>
                <th>Status</th>
                <th>Photo</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="tv-no-data">Loading…</td>
                </tr>
              ) : tvData.length > 0 ? (
                tvData.map((item, index) => (
                  <tr key={item.id}>
                    <td>{startIndex + index + 1}</td>
                    <td>
                      <div className="tv-name-cell">
                        <span className="tv-avatar">
                          <FaUserCircle />
                        </span>
                        <span className="tv-name-text">{item.motherName}</span>
                      </div>
                    </td>
                    <td className="tv-student-cell">{item.studentName}</td>
                    <td className="tv-school-cell">{item.school}</td>
                    <td>
                      <span className="tv-class-pill">{item.className}</span>
                    </td>
                    <td className="tv-amount">₹{item.amount.toLocaleString("en-IN")}</td>
                    <td>
                      <span className="tv-village-cell">
                        <FaMapMarkerAlt /> {item.village}
                      </span>
                    </td>
                    <td>{item.mandal}</td>
                    <td>
                      <span className="tv-date-cell">
                        <FaCalendarAlt /> {item.date}
                      </span>
                    </td>
                    <td>
                      <span className={`tv-status ${item.status === "approved" ? "tv-approved" : "tv-pending"}`}>
                        {item.status === "approved" ? <FaCheckCircle /> : <FaRegClock />} {STATUS_LABEL[item.status] || item.status}
                      </span>
                    </td>
                    <td>
                      {item.photoUrl ? (
                        <button
                          type="button"
                          onClick={() => openImagePreview(item.photoUrl)}
                          title="View photo"
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#16a34a",
                            cursor: "pointer",
                            fontSize: "18px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <FaImage />
                        </button>
                      ) : (
                        <span style={{ color: "#9ca3af", fontSize: "13px" }}>—</span>
                      )}
                    </td>
                    <td className="tv-action-cell">
                      <button className="tv-edit-btn" onClick={() => openEditModal(item)}>
                        <FaEdit /> Edit
                      </button>
                      <button className="tv-delete-btn" onClick={() => openDeleteModal(item)}>
                        <FaTrashAlt /> Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="12" className="tv-no-data">No matching beneficiaries found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="tv-footer">
          <p className="tv-footer-text">
            {totalCount > 0
              ? `Showing ${startIndex + 1} to ${endIndex} of ${totalCount} beneficiaries`
              : "Showing 0 beneficiaries"}
          </p>

          <div className="tv-pagination">
            <button className="tv-page-btn" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
              <FaChevronLeft />
            </button>

            {getPageNumbers().map((page, i) =>
              page === "..." ? (
                <span key={`dots-${i}`} className="tv-page-dots">...</span>
              ) : (
                <button
                  key={page}
                  className={`tv-page-btn ${currentPage === page ? "tv-page-active" : ""}`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              ),
            )}

            <button
              className="tv-page-btn"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <FaChevronRight />
            </button>
          </div>
        </div>

        <div className="tv-scheme-banner">
          <div className="tv-banner-icon-left">
            <FaHandHoldingHeart />
          </div>
          <div className="tv-banner-text">
            <h4>Thalliki Vandanam Scheme</h4>
            <p>Financial assistance of ₹15,000 is provided to eligible mothers for the education and welfare of their children.</p>
          </div>
        </div>
      </div>

      {/* ---------------- EDIT MODAL — now includes ALL update fields ---------------- */}
      {editItem && editForm && (
        <div className="tv-modal-overlay" onClick={closeEditModal}>
          <div className="tv-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="tv-modal-header">
              <h3>Edit Beneficiary</h3>
              <button className="tv-modal-close" onClick={closeEditModal}>
                <FaTimes />
              </button>
            </div>

            <form className="tv-modal-form" onSubmit={handleEditSave}>
              <div className="tv-form-grid">
                <div className="tv-form-row">
                  <label>Mother's Name *</label>
                  <input
                    type="text"
                    value={editForm.motherName}
                    onChange={(e) => handleEditChange("motherName", e.target.value)}
                    required
                  />
                </div>

                <div className="tv-form-row">
                  <label>Relation</label>
                  <input
                    type="text"
                    value={editForm.relation}
                    onChange={(e) => handleEditChange("relation", e.target.value)}
                  />
                </div>

                <div className="tv-form-row">
                  <label>Voter ID (EPIC No.)</label>
                  <input
                    type="text"
                    value={editForm.voterId}
                    onChange={(e) => handleEditChange("voterId", e.target.value)}
                  />
                </div>

                <div className="tv-form-row">
                  <label>Student Name *</label>
                  <input
                    type="text"
                    value={editForm.studentName}
                    onChange={(e) => handleEditChange("studentName", e.target.value)}
                    required
                  />
                </div>

                <div className="tv-form-row">
                  <label>School Name *</label>
                  <input
                    type="text"
                    value={editForm.school}
                    onChange={(e) => handleEditChange("school", e.target.value)}
                    required
                  />
                </div>

                <div className="tv-form-row">
                  <label>Class / Grade *</label>
                  <input
                    type="text"
                    value={editForm.className}
                    onChange={(e) => handleEditChange("className", e.target.value)}
                    required
                  />
                </div>

                {/* FIX: Aadhaar must be re-entered in full — the backend
                    rejects the whole update without it. */}
                <div className="tv-form-row">
                  <label>Aadhaar Number * (re-enter to save)</label>
                  <input
                    type="text"
                    maxLength="12"
                    placeholder="Enter 12-digit Aadhaar Number"
                    value={editForm.aadhaar}
                    onChange={(e) => handleEditChange("aadhaar", e.target.value)}
                    required
                  />
                  <small style={{ color: "#9ca3af", fontSize: "12px", marginTop: "4px", display: "block" }}>
                    On file: {editItem.aadhaarMasked || "—"}. The full number must be re-entered here every time — the server does not accept updates without it.
                  </small>
                </div>

                <div className="tv-form-row">
                  <label>Mobile Number</label>
                  <input
                    type="tel"
                    maxLength="10"
                    value={editForm.mobile}
                    onChange={(e) => handleEditChange("mobile", e.target.value)}
                  />
                </div>

                <div className="tv-form-row">
                  <label>Bank Account Number</label>
                  <input
                    type="text"
                    value={editForm.bankAccount}
                    onChange={(e) => handleEditChange("bankAccount", e.target.value)}
                  />
                </div>

                <div className="tv-form-row">
                  <label>IFSC Code</label>
                  <input
                    type="text"
                    value={editForm.ifsc}
                    onChange={(e) => handleEditChange("ifsc", e.target.value)}
                  />
                </div>

                <div className="tv-form-row">
                  <label>Amount (₹) *</label>
                  <input
                    type="number"
                    value={editForm.amount}
                    onChange={(e) => handleEditChange("amount", e.target.value)}
                    required
                  />
                </div>

                <div className="tv-form-row">
                  <label>Village * (re-enter to save)</label>
                  <input
                    type="text"
                    placeholder="Enter Village Name"
                    value={editForm.villageName}
                    onChange={(e) => handleEditChange("villageName", e.target.value)}
                    required
                  />
                  <small style={{ color: "#9ca3af", fontSize: "12px", marginTop: "4px", display: "block" }}>
                    Must exactly match an existing village name under the selected Mandal.
                  </small>
                </div>

                <div className="tv-form-row">
                  <label>Mandal *</label>
                  <select value={editForm.mandalName} onChange={(e) => handleEditChange("mandalName", e.target.value)}>
                    {MANDAL_NAMES.map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>
                </div>

                <div className="tv-form-row">
                  <label>Date</label>
                  <input
                    type="date"
                    value={editForm.date}
                    onChange={(e) => handleEditChange("date", e.target.value)}
                    required
                  />
                </div>

                <div className="tv-form-row">
                  <label>Status *</label>
                  <select value={editForm.status} onChange={(e) => handleEditChange("status", e.target.value)}>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                    ))}
                  </select>
                </div>

                <div className="tv-form-row">
                  <label>Photo URL</label>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={editForm.photoUrl}
                    onChange={(e) => handleEditChange("photoUrl", e.target.value)}
                  />
                </div>

                <div className="tv-form-row">
                  <label>Supporting Document URL</label>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={editForm.documentUrl}
                    onChange={(e) => handleEditChange("documentUrl", e.target.value)}
                  />
                </div>
              </div>

              {editForm.photoUrl && (
                <div style={{ marginTop: "10px" }}>
                  <img
                    src={editForm.photoUrl}
                    alt="Beneficiary preview"
                    style={{
                      width: "110px",
                      height: "80px",
                      objectFit: "cover",
                      borderRadius: "6px",
                      border: "1px solid #e2e8f0",
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}

              <div className="tv-form-row" style={{ marginTop: "14px" }}>
                <label>Remarks</label>
                <textarea
                  rows="3"
                  value={editForm.remarks}
                  onChange={(e) => handleEditChange("remarks", e.target.value)}
                />
              </div>

              <div className="tv-modal-actions">
                <button type="button" className="tv-modal-cancel-btn" onClick={closeEditModal}>
                  Cancel
                </button>
                <button type="submit" className="tv-modal-save-btn" disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteItem && (
        <div className="tv-modal-overlay" onClick={closeDeleteModal}>
          <div className="tv-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tv-confirm-icon">
              <FaTrashAlt />
            </div>
            <h3 className="tv-confirm-title">Delete this beneficiary?</h3>
            <p className="tv-confirm-text">
              Are you sure you want to delete <strong>{deleteItem.motherName}</strong>'s record (student:{" "}
              <strong>{deleteItem.studentName}</strong>) from the beneficiary list? This action cannot be undone.
            </p>

            <div className="tv-confirm-actions">
              <button className="tv-confirm-cancel-btn" onClick={closeDeleteModal}>
                Cancel
              </button>
              <button className="tv-confirm-delete-btn" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {imagePreviewUrl && (
        <div className="tv-modal-overlay" onClick={closeImagePreview}>
          <div
            className="tv-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "480px", padding: 0 }}
          >
            <div className="tv-modal-header">
              <h3>Beneficiary Photo</h3>
              <button className="tv-modal-close" onClick={closeImagePreview}>
                <FaTimes />
              </button>
            </div>
            <div style={{ padding: 20, textAlign: "center" }}>
              <img
                src={imagePreviewUrl}
                alt="Beneficiary"
                style={{ maxWidth: "100%", maxHeight: "60vh", borderRadius: 8 }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = FALLBACK_IMG;
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThallikiVandanamTable;